import type { JSX } from 'react';

import { cn } from '@/lib/utils.js';

import type { DiffLine } from './line-diff.js';

/**
 * The marker each row wears. A real character in the document, not an `aria-hidden` flourish: it is
 * the part of the diff that still works with the colour taken away, so it has to reach a screen
 * reader too.
 */
const MARKERS: Record<DiffLine['kind'], string> = {
  add: '+',
  remove: '−',
  context: '\u00a0',
};

/**
 * A difference, rendered so it survives being read in greyscale: every row carries a literal `−`, `+`
 * or space of its own, and the tint is decoration on top (canon §16.6). Screen readers get the same
 * information through the marker, which is a real character rather than a background colour.
 */
export function MemoryDiff({
  lines,
  className,
}: {
  lines: readonly DiffLine[];
  className?: string;
}): JSX.Element | null {
  if (lines.length === 0) return null;
  return (
    <div
      data-testid="memory-diff"
      className={cn(
        'overflow-hidden rounded-md border border-border/60 font-mono text-xs',
        className,
      )}
    >
      {lines.map((line, index) => (
        <div
          key={`${line.kind}-${index}-${line.text}`}
          data-diff-kind={line.kind}
          className={cn(
            'flex gap-2 px-2 py-1',
            line.kind === 'add' && 'bg-success-500/10 text-success-600',
            line.kind === 'remove' && 'bg-destructive/10 text-destructive',
          )}
        >
          <span className="select-none">{MARKERS[line.kind]}</span>
          <span className="whitespace-pre-wrap break-words">{line.text}</span>
        </div>
      ))}
    </div>
  );
}
